<script setup>
import { ref, computed, watch } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Download, Printer, FileSpreadsheet, Loader2, FileText, History } from 'lucide-vue-next'
import { toast } from '@/components/ui/toast'
import { API, HOST } from '@/utils/http'

// Configure marked
marked.setOptions({
  breaks: true,
  gfm: true,
  smartLists: true,
  smartypants: true
})

const props = defineProps({
  report: {
    type: Object,
    default: null
  },
  isUpdating: {
    type: Boolean,
    default: false
  },
  orgId: {
    type: [String, Number],
    required: true
  },
  sessionCode: {
    type: String,
    required: true
  },
  isStreaming: {
    type: Boolean,
    default: false
  },
  streamingReport: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['version-changed'])

// Version management
const versions = ref([])
const selectedVersionNumber = ref(null)
const isLoadingVersions = ref(false)

// Fetch version data by version number
const fetchVersionData = async (versionNumber) => {
  if (!versionNumber) return null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${props.orgId}/chats/${props.sessionCode}/report/versions/${versionNumber}`
    })
    return response.data
  } catch (error) {
    console.error('Error fetching version data:', error)
    throw error
  }
}

// Load versions
const loadVersions = async () => {
  if (!props.sessionCode) return

  isLoadingVersions.value = true
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${props.orgId}/chats/${props.sessionCode}/report/versions`
    })
    versions.value = response.data || []

    // Auto-select and load version data
    let versionToLoad = null

    // Prioritize report's version if it exists and has content
    if (props.report?.version_number && props.report?.report_content) {
      versionToLoad = props.report.version_number
      selectedVersionNumber.value = versionToLoad
    } else if (versions.value.length > 0) {
      // Find current version or use the last one (latest)
      const currentVersion = versions.value.find(v => v.is_current)
      const latestVersion = versions.value[versions.value.length - 1]
      versionToLoad = (currentVersion || latestVersion)?.version_number

      // Fetch and emit the version data if we don't have report content
      if (versionToLoad && !props.report?.report_content) {
        const versionData = await fetchVersionData(versionToLoad)
        selectedVersionNumber.value = versionToLoad
        emit('version-changed', versionData)
        return
      }

      selectedVersionNumber.value = versionToLoad
    }
  } catch (error) {
    console.error('Error loading versions:', error)
  } finally {
    isLoadingVersions.value = false
  }
}

// Switch version
const switchVersion = async (versionNumber) => {
  if (!versionNumber || versionNumber === selectedVersionNumber.value) return

  try {
    const versionData = await fetchVersionData(versionNumber)
    selectedVersionNumber.value = versionNumber
    emit('version-changed', versionData)
  } catch (error) {
    console.error('Error switching version:', error)
    toast({
      title: 'Error',
      description: 'Failed to load version',
      variant: 'destructive'
    })
  }
}

// Watch for report code changes to reload versions (not version number changes)
watch(() => props.report?.code, (newCode, oldCode) => {
  if (newCode && newCode !== oldCode) {
    loadVersions()
  }
}, { immediate: true })

const renderedContent = computed(() => {
  // Show streaming content if currently streaming
  if (props.isStreaming && props.streamingReport) {
    try {
      const htmlContent = marked.parse(props.streamingReport)
      return DOMPurify.sanitize(htmlContent)
    } catch (error) {
      // If markdown parsing fails for partial content, show raw text
      return DOMPurify.sanitize(props.streamingReport)
    }
  }

  // Otherwise show the saved report content
  if (!props.report?.report_content) return ''
  const htmlContent = marked.parse(props.report.report_content)
  return DOMPurify.sanitize(htmlContent)
})

const canDownload = computed(() => {
  return props.report?.status === 'completed' && props.report?.report_content
})

const currentVersionLabel = computed(() => {
  if (!props.report?.version_number) return 'No version'
  const isCurrent = versions.value.find(v => v.version_number === props.report.version_number)?.is_current
  return `Version ${props.report.version_number}${isCurrent ? ' (current)' : ''}`
})

const downloadMarkdown = () => {
  if (!props.report?.report_content) return

  try {
    const blob = new Blob([props.report.report_content], { type: 'text/markdown' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `report_${props.report.code}_${Date.now()}.md`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    toast({
      title: 'Downloaded',
      description: 'Report downloaded as Markdown',
      variant: 'default'
    })
  } catch (error) {
    console.error('Error downloading:', error)
    toast({
      title: 'Error',
      description: 'Failed to download report',
      variant: 'destructive'
    })
  }
}

const downloadCSV = () => {
  if (!props.report?.csv_url) {
    toast({
      title: 'No CSV',
      description: 'CSV data not available for this report',
      variant: 'destructive'
    })
    return
  }

  try {
    const link = document.createElement('a')
    link.href = props.report.csv_url
    link.target = '_blank'
    link.download = `data_${props.report.code}_${Date.now()}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()

    toast({
      title: 'Downloaded',
      description: 'CSV data downloaded',
      variant: 'default'
    })
  } catch (error) {
    console.error('Error downloading CSV:', error)
    toast({
      title: 'Error',
      description: 'Failed to download CSV',
      variant: 'destructive'
    })
  }
}

const printReport = () => {
  if (!props.report?.report_content) return

  try {
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      toast({
        title: 'Blocked',
        description: 'Please allow popups to print',
        variant: 'destructive'
      })
      return
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>AI Report - ${props.report.code}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
            .header {
              border-bottom: 2px solid #eee;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>AI Report</h1>
            <p>Generated: ${new Date(props.report.created_at).toLocaleString()}</p>
          </div>
          <div class="content">
            ${renderedContent.value}
          </div>
        </body>
      </html>
    `

    printWindow.document.write(printContent)
    printWindow.document.close()
    printWindow.focus()
    printWindow.print()
  } catch (error) {
    console.error('Error printing:', error)
    toast({
      title: 'Error',
      description: 'Failed to print report',
      variant: 'destructive'
    })
  }
}
</script>

<template>
  <div class="flex flex-col max-h-full">
    <!-- Header with Actions -->
    <div class="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0 min-w-0">
      <div class="flex items-center gap-2 min-w-0 overflow-hidden">
        <h2 class="text-sm font-semibold truncate">Report</h2>
        <span v-if="isUpdating" class="text-xs text-orange-600 flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
          <Loader2 class="h-3 w-3 animate-spin" />
          Updating...
        </span>
      </div>

      <div class="flex items-center gap-2 flex-shrink-0 ml-2">
        <!-- Version Selector -->
        <div v-if="versions.length > 0" class="flex items-center gap-2">
          <Select
            :model-value="String(selectedVersionNumber)"
            @update:model-value="(val) => switchVersion(Number(val))"
          >
            <SelectTrigger class="w-[160px] h-8 text-xs">
              <SelectValue :placeholder="currentVersionLabel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="version in versions"
                :key="version.version_number"
                :value="String(version.version_number)"
              >
                Version {{ version.version_number }}{{ version.is_current ? ' (latest)' : '' }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          v-if="report?.csv_url"
          @click="downloadCSV"
          variant="ghost"
          size="sm"
          :disabled="!canDownload"
        >
          <FileSpreadsheet class="h-4 w-4" />
        </Button>
        <Button
          @click="downloadMarkdown"
          variant="ghost"
          size="sm"
          :disabled="!canDownload"
        >
          <Download class="h-4 w-4" />
        </Button>
        <Button
          @click="printReport"
          variant="ghost"
          size="sm"
          :disabled="!canDownload"
        >
          <Printer class="h-4 w-4" />
        </Button>
      </div>
    </div>

    <!-- Report Content -->
    <div class="overflow-y-auto overflow-x-hidden px-6 py-6">
      <!-- Empty/Pending State -->
      <div v-if="(!report || (report?.status === 'pending' && !report?.report_content)) && !isStreaming && !streamingReport" class="flex items-center justify-center min-h-[60vh]">
        <div class="text-center text-muted-foreground">
          <FileText class="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p class="text-lg mb-2">No report yet</p>
          <p class="text-sm">Send a message to generate analysis</p>
        </div>
      </div>

      <!-- Streaming Report Content -->
      <div v-else-if="isStreaming && streamingReport" class="relative">
        <div class="absolute top-2 right-2 flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-medium z-10">
          <Loader2 class="h-3 w-3 animate-spin" />
          Working...
        </div>
        <div class="prose prose-sm max-w-none break-words overflow-wrap-anywhere w-full">
          <div v-html="renderedContent" class="break-words overflow-hidden"></div>
        </div>
      </div>

      <!-- Completed Report Content -->
      <div v-else-if="report?.report_content && report.status === 'completed'" class="prose prose-sm max-w-none break-words overflow-wrap-anywhere w-full">
        <div v-html="renderedContent" class="break-words overflow-hidden"></div>
      </div>

      <!-- Generating State (waiting for stream to start) -->
      <div v-else-if="(report?.status === 'pending' && (report?.report_content || isStreaming)) || isUpdating" class="flex items-center justify-center min-h-[60vh]">
        <div class="text-center">
          <Loader2 class="h-12 w-12 mx-auto mb-4 animate-spin text-purple-600" />
          <p class="text-lg font-medium">Generating analysis...</p>
          <p class="text-sm text-muted-foreground mt-2">This may take a moment</p>
        </div>
      </div>

      <!-- Failed State -->
      <div v-else-if="report?.status === 'failed'" class="flex items-center justify-center min-h-[60vh]">
        <div class="text-center text-red-600">
          <p class="text-lg font-medium">Generation failed</p>
          <p class="text-sm text-muted-foreground mt-2">Please try sending your message again</p>
        </div>
      </div>
    </div>
  </div>
</template>
